import SafeUncontrolledPopover from 'components/SafeUncontrolledPopover';
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Col, ModalBody, ModalHeader } from 'reactstrap';
import AILoader from 'views/AI/widgets/AILoader';
import Loading from 'views/Widgets/Loading';
import ValidatedForm from '../../Forms/ValidatedForm';
import ModalEditor from '../../Modals/ModalEditor';

interface Props {
  name: string;
  value: string;
  title: string;
  inputs: any[];
  buttonText?: ReactNode;
  buttonClassName?: string;
  buttonStyle?: React.CSSProperties;
  columnStyle?: string;
  submitButtonTitle: string;
  loaderText: string;
  WrapperComponent?: React.ComponentType<{ children: React.ReactNode }>;
  onCallback?: (...args: any[]) => any;
  onSubmit?: (...args: any[]) => any;
  onOpen?: () => void;
  mockResponse?: any;
  type?: 'POPOVER' | 'MODAL';
}

const InputAuxiliary: FC<Props> = ({
  inputs,
  title,
  buttonText,
  buttonClassName,
  buttonStyle,
  columnStyle = 'col-auto ps-0 ms-0 justify-content-end',
  submitButtonTitle,
  WrapperComponent,
  onCallback = (_args) => {},
  onSubmit,
  onOpen,
  mockResponse,
  type = 'MODAL',
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  const [object, setObject] = useState<Record<string, any> | undefined>();
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Initialize form object with default values from inputs
  useEffect(() => {
    if ((isModalOpen || isPopoverOpen) && !object) {
      const defaultValues = {};
      inputs.forEach((input) => {
        if (input.defaultValue !== undefined && input.name) {
          defaultValues[input.name] = input.defaultValue;
        }
      });
      if (Object.keys(defaultValues).length > 0) {
        setObject(defaultValues);
      }
    }
  }, [isModalOpen, isPopoverOpen, inputs, object]);

  const handleClose = useCallback(() => {
    setErrorMessage(undefined);
    setIsBusy(false);
    setIsModalOpen(false);
    setIsPopoverOpen(false);
    setObject(undefined);
  }, []);
  const handleOpen = useCallback(() => {
    onOpen?.();
    if (type === 'POPOVER') setIsPopoverOpen(true);
    if (type === 'MODAL') setIsModalOpen(true);
  }, [type, onOpen]);

  const handleError = useCallback((errorMessage: string) => {
    setErrorMessage(errorMessage);
    setIsBusy(false);
  }, []);

  const handleSave = useCallback(() => {
    setErrorMessage(undefined);
    setIsBusy(true);
    if (onSubmit) {
      onSubmit?.(object, onCallback, handleClose, handleError);
    } else if (onCallback) {
      onCallback(mockResponse ? mockResponse : object);
      handleClose();
    }
  }, [onSubmit, onCallback, object, handleClose, mockResponse, handleError]);

  const Wrapper = WrapperComponent ?? React.Fragment;

  return (
    <Col className={columnStyle}>
      <Wrapper>
        <button
          type="button"
          className={
            buttonClassName ?? 'btn btn-sm mt-2 btn-light auxiliary-button px-2'
          }
          style={{
            borderRadius: '2rem',
            filter: 'drop-shadow(2px 2px 3px #eeeeee)',
            ...buttonStyle,
          }}
          ref={buttonRef}
          onClick={handleOpen}
        >
          <i className="fe fe-zap" />{' '}
          {buttonText ?? (
            <FormattedMessage
              id="app.views.widgets.inputs.input_auxiliary.input_auxiliary.ai_label"
              defaultMessage="Use AI"
            />
          )}
        </button>

        {isModalOpen && (
          <ModalEditor
            hideSubmitButton={true}
            title={title}
            isOpen={isModalOpen}
            toggle={handleClose}
            callback={onCallback}
          >
            <>
              <ValidatedForm
                inForm={true}
                inputs={inputs}
                onChange={setObject}
                hideSubmitButton={true}
                object={object}
                callback={() => {}}
              />
              <div>
                {isBusy && (
                  <Loading
                    message={<AILoader footerElementShowAfterMillis={20000} />}
                  />
                )}
                {errorMessage && (
                  <div className={`text-center pt-2 message error`}>
                    <span className="text-danger">{errorMessage}</span>
                  </div>
                )}
                <Button
                  color="primary"
                  onClick={handleSave}
                  className="w-100 mt-4"
                  disabled={isBusy}
                >
                  {submitButtonTitle}
                </Button>
              </div>
            </>
          </ModalEditor>
        )}
        {isPopoverOpen && (
          <SafeUncontrolledPopover
            className="popover-lg"
            target={buttonRef}
            delay={0}
            placement="right-start"
            isOpen={isPopoverOpen}
          >
            <ModalHeader toggle={handleClose}>{title}</ModalHeader>
            <ModalBody>
              <ValidatedForm
                inForm={true}
                inputs={inputs}
                onChange={setObject}
                hideSubmitButton={true}
                object={object}
                callback={() => {}}
              />
              <div>
                {isBusy && (
                  <Loading
                    message={<AILoader footerElementShowAfterMillis={20000} />}
                  />
                )}
                {errorMessage && (
                  <div className={`text-center pt-2 message error`}>
                    <span className="text-danger">{errorMessage}</span>
                  </div>
                )}
                <Button
                  color="primary"
                  onClick={handleSave}
                  className="w-100 mt-0"
                  disabled={isBusy}
                >
                  {submitButtonTitle}
                </Button>
              </div>
            </ModalBody>
          </SafeUncontrolledPopover>
        )}
      </Wrapper>
    </Col>
  );
};

export default React.memo(InputAuxiliary);
